import { useEffect, useState } from 'react';
import type { LiquidacionDePlataforma, PartnerDePlataforma } from '../../api/ops/backoffice';
import { Boton } from '../../componentes/Boton';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { dinero } from '../../logica/dinero';
import { estadoDeError } from '../../logica/estados';
import { SinPermiso } from '../SinPermiso';
import type { PropsSeccion } from '../tipos';
import { errorDelMarco, todasLasPaginas } from './carga';
import { lineaPartnerLiquidacion, lineaSolicitud, mesDeLiquidacion, notaLiquidacion, notaSolicitudes, periodoDe, SIN_SOLICITUDES, textoBase, textoDecidida, tituloLiquidacion } from './logica';
import { VentanaDatosLegales, VentanaDecision, VentanaPago } from './Ventanas';
import './partners.css';

/**
 * **Partners** del backoffice (1.23, solo Superadmin): las solicitudes por revisar con «Aprobar» y «Rechazar»
 * (`decidePartnerApplication`, motivo opcional al rechazar) y la liquidación del último mes cerrado
 * (`listPlatformPartnerSettlements`, G1086) con «Pagada» y «Datos legales» de cada partner (G237). Atribuir a mano se
 * hace en la ficha del negocio (Negocios).
 *
 * Al entrar se ve el último mes cerrado (G1147, `mesDeLiquidacion`). La cabecera dice «2 por pagar · 95,20 €»
 * (`pending_count` y `pending_total`, G1145), cada importe lleva «20 % de <base>» (`commission_base`) y «Marcar pagada»
 * (`markSettlementPaid`) pide la «Referencia de la transferencia» (G1146).
 */
interface Datos {
    partners: PartnerDePlataforma[];
    liquidaciones: LiquidacionDePlataforma[];
    porPagar: { cuantas: number; total: LiquidacionDePlataforma['total'] };
}

type Ventana =
    | { tipo: 'decision'; partner: PartnerDePlataforma; decision: 'approve' | 'reject' }
    | { tipo: 'legal'; partner: PartnerDePlataforma }
    | { tipo: 'pago'; liquidacion: LiquidacionDePlataforma };

export function Partners(p: PropsSeccion) {
    const { ops, movil, onSesionCaducada, onFaltanDosPasos } = p;
    const [mes] = useState(() => mesDeLiquidacion(new Date()));
    const [estado, setEstado] = useState<{ datos: Datos | null; error: unknown }>({ datos: null, error: null });
    const [vuelta, setVuelta] = useState(0);
    const [decididas, setDecididas] = useState<Readonly<Record<string, 'approve' | 'reject'>>>({});
    const [ventana, setVentana] = useState<Ventana | null>(null);

    useEffect(() => {
        let vivo = true;
        const periodo = periodoDe(mes);
        // Lo pendiente (G1145) no depende de la página: se toma de la primera.
        let porPagar: Datos['porPagar'] = { cuantas: 0, total: { amount: 0, currency: 'EUR' } };
        Promise.all([
            todasLasPaginas((cursor) => ops.listPartners({ cursor, limit: 100 })),
            todasLasPaginas((cursor) =>
                ops.listPlatformPartnerSettlements({ period: periodo, cursor, limit: 100 }).then((r) => {
                    if (cursor === undefined) {
                        porPagar = { cuantas: r.pending_count, total: r.pending_total };
                    }
                    return r;
                }),
            ),
        ])
            .then(([partners, liquidaciones]) => {
                if (vivo) {
                    setEstado({ datos: { partners, liquidaciones, porPagar }, error: null });
                }
            })
            .catch((error: unknown) => {
                if (vivo && !errorDelMarco(error, { onSesionCaducada, onFaltanDosPasos })) {
                    setEstado({ datos: null, error });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, mes, vuelta, onSesionCaducada, onFaltanDosPasos]);

    const { datos, error } = estado;
    if (error !== null) {
        return estadoDeError(error).tipo === 'sin-permiso' ? (
            <SinPermiso seccion="Partners" superadmin={p.superadmin} movil={movil} onIr={null} />
        ) : (
            <EstadoError
                seccion="Partners"
                codigoSoporte={estadoDeError(error).codigoSoporte}
                movil={movil}
                onReintentar={() => {
                    setEstado({ datos: null, error: null });
                    setVuelta((v) => v + 1);
                }}
            />
        );
    }
    if (datos === null) {
        return <EstadoCargando seccion="Partners" />;
    }

    // Las solicitudes que se deciden se quedan en la lista con «Aprobada» o «Rechazada», como en el prototipo.
    const solicitudes = datos.partners.filter((x) => x.status === 'applied' || decididas[x.id] !== undefined);
    const pendientes = solicitudes.filter((x) => decididas[x.id] === undefined).length;
    const porId = new Map(datos.partners.map((x) => [x.id, x]));

    return (
        <div className="rv-bop">
            <div className="rv-bop-cab">
                <strong>Solicitudes</strong>
                <span>{notaSolicitudes(pendientes)}</span>
            </div>
            {solicitudes.length === 0 ? (
                <div className="rv-bop-vacia">
                    <Icono nombre="inbox" />
                    <span>{SIN_SOLICITUDES}</span>
                </div>
            ) : (
            <div className="rv-bop-caja">
                {solicitudes.map((s) => {
                    const decidida = decididas[s.id];
                    return (
                        <div key={s.id} className="rv-bop-fila" role="group" aria-label={s.legal_name}>
                            <span className="rv-bop-textos">
                                <strong>{s.legal_name}</strong>
                                <span>{lineaSolicitud(s)}</span>
                            </span>
                            {decidida !== undefined ? (
                                <span className="rv-bop-hecha">
                                    <Icono nombre={decidida === 'approve' ? 'check' : 'x'} />
                                    {textoDecidida(decidida)}
                                </span>
                            ) : (
                                <span className="rv-bop-botones">
                                    <Boton
                                        tipo="secundario"
                                        icono="check"
                                        movil={movil}
                                        onClick={() => {
                                            setVentana({ tipo: 'decision', partner: s, decision: 'approve' });
                                        }}
                                    >
                                        Aprobar
                                    </Boton>
                                    <Boton
                                        tipo="secundario"
                                        rojo
                                        icono="x"
                                        movil={movil}
                                        onClick={() => {
                                            setVentana({ tipo: 'decision', partner: s, decision: 'reject' });
                                        }}
                                    >
                                        Rechazar
                                    </Boton>
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
            )}

            <div className="rv-bop-cab rv-bop-cab--liq">
                <strong>{tituloLiquidacion(mes)}</strong>
                <span>{notaLiquidacion(datos.porPagar.cuantas, datos.porPagar.total)}</span>
            </div>
            <div className="rv-bop-caja">
                {datos.liquidaciones.map((l, i) => {
                    const partner = porId.get(l.partner_id);
                    return (
                        <div key={l.id} className="rv-bop-fila rv-bop-fila--liq" style={{ animationDelay: `${String(i * 50)}ms` }} role="group" aria-label={l.partner_name}>
                            <span className="rv-bop-baldosa">
                                <Icono nombre="handshake" />
                            </span>
                            <span className="rv-bop-textos">
                                <strong>{l.partner_name}</strong>
                                <span>{lineaPartnerLiquidacion(partner, l.status === 'paid' ? (l.payment_reference ?? null) : null)}</span>
                            </span>
                            <span className="rv-bop-importe">
                                <strong>{dinero(l.total)}</strong>
                                {l.commission_base !== undefined && <span>{textoBase(l.commission_base)}</span>}
                            </span>
                            {l.status === 'paid' && (
                                <span className="rv-bop-hecha">
                                    <Icono nombre="check" />
                                    Pagada
                                </span>
                            )}
                            {partner !== undefined && (
                                <Boton
                                    tipo="fantasma"
                                    icono="landmark"
                                    movil={movil}
                                    aria-label={`Datos legales de ${l.partner_name}`}
                                    onClick={() => {
                                        setVentana({ tipo: 'legal', partner });
                                    }}
                                >
                                    Datos legales
                                </Boton>
                            )}
                            {l.status === 'prepared' && (
                                <Boton
                                    tipo="secundario"
                                    icono="wallet"
                                    movil={movil}
                                    aria-label={`Marcar pagada la liquidación de ${l.partner_name}`}
                                    onClick={() => {
                                        setVentana({ tipo: 'pago', liquidacion: l });
                                    }}
                                >
                                    Marcar pagada
                                </Boton>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="rv-bop-nota">La liquidación de un mes se cierra el día 5 del mes siguiente y se paga por transferencia antes del 15. Si una factura se reembolsa, su comisión se descuenta de la siguiente.</div>

            {ventana?.tipo === 'decision' && (
                <VentanaDecision
                    ops={ops}
                    movil={movil}
                    partner={ventana.partner}
                    decision={ventana.decision}
                    onCerrar={() => {
                        setVentana(null);
                    }}
                    onHecho={() => {
                        setDecididas((d) => ({ ...d, [ventana.partner.id]: ventana.decision }));
                        setVentana(null);
                    }}
                />
            )}
            {ventana?.tipo === 'pago' && (
                <VentanaPago
                    ops={ops}
                    movil={movil}
                    liquidacion={ventana.liquidacion}
                    mes={mes}
                    onCerrar={() => {
                        setVentana(null);
                    }}
                    onHecho={() => {
                        setVentana(null);
                        setVuelta((v) => v + 1);
                    }}
                />
            )}
            {ventana?.tipo === 'legal' && (
                <VentanaDatosLegales
                    ops={ops}
                    movil={movil}
                    partner={ventana.partner}
                    onCerrar={() => {
                        setVentana(null);
                    }}
                    onHecho={(nuevo) => {
                        setEstado((e) => (e.datos === null ? e : { ...e, datos: { ...e.datos, partners: e.datos.partners.map((x) => (x.id === nuevo.id ? nuevo : x)) } }));
                        setVentana(null);
                    }}
                />
            )}
        </div>
    );
}
